import { createSelector } from "reselect";
import { orderByName } from "../utils/orderByName";
import { getQuestionStems } from "./questionStemSelectors";

function orderByDate(a, b) {
  return new Date(b.createdAt) - new Date(a.createdAt);
}

function orderByScore(a, b) {
  return b.score === a.score ? a.id - b.id : b.score - a.score;
}

const getMocks = (state) => state.entities.mocks;
const getMockRankings = (state) => state.entities.mockRankings;
const getFetchedIds = (state) => state.mockList.mockIds;
const getRankingIds = (state) => state.mockList.rankingIds;
const getAnswers = (state) => state.entities.answers;
const getQuestions = (state) => state.entities.questions;
const getMockTemplates = (state) => state.entities.mockTemplates;
const getId = (_, id) => id;
const getProps = (_, props) => props;
export const getCurrentMockId = (state) => state.mock.currentMockId;
export const getMockId = (state, id) => id || state.mock.currentMockId;

export const getAllMocks = createSelector(getMocks, (mocks) =>
  Object.values(mocks).sort(orderByDate)
);

export const makeHasMocks = createSelector(
  getAllMocks,
  (mocks) => !!mocks.length
);

export const getMock = createSelector(
  getMocks,
  getMockId,
  (mocks, id) => mocks[id]
);

export const getMocksByFetchedIds = createSelector(
  getMocks,
  getFetchedIds,
  (mocks, ids) =>
    Object.values(mocks)
      .filter((mock) => ids.includes(mock.id))
      .sort(orderByDate)
);

export const getAllMockRankings = createSelector(
  getMockRankings,
  (mockRankings) => Object.values(mockRankings)
);

export const getMocksForRanking = createSelector(
  getAllMockRankings,
  getId,
  getRankingIds,
  (mockRankings, id, ids) =>
    mockRankings
      .filter((mock) => mock.mockTemplateId == id && ids.includes(mock.id))
      .sort(orderByScore)
);

export const getCurrentMock = createSelector(
  getMocks,
  getCurrentMockId,
  (mocks, currentMockId) => mocks[currentMockId]
);

export const currentMockQuestions = createSelector(
  getCurrentMock,
  getQuestions,
  (mock, questions) => {
    if (!mock || !mock.questionIds) return [];
    return mock.questionIds
      .map((questionId) => questions[questionId])
      .filter(Boolean);
  }
);

export const currentMockAnswers = createSelector(
  getCurrentMock,
  getAnswers,
  (mock, answers) =>
    Object.values(answers).filter((answer) => answer?.mockId === mock?.id)
);

export const showAnswer = createSelector(
  getCurrentMock,
  (mock) => !mock?.exam || !!mock?.finishedAt
);

export const makeIsLastAnswer = createSelector(
  currentMockQuestions,
  currentMockAnswers,
  (questions, answers) =>
    questions &&
    answers &&
    (questions?.length - answers?.length == 1 ||
      questions?.length == answers?.length)
);

export const makeIsComplete = createSelector(
  [getCurrentMock, getAnswers, currentMockQuestions, getQuestionStems],
  (mock, answers, questions, allStems) => {
    if (!mock || !questions || questions.length === 0) {
      return false;
    }

    const mockAnswers = Object.values(answers || {}).filter(
      (answer) => answer?.mockId != null && Number(answer.mockId) === Number(mock.id)
    );

    const findPromptById = (questionId, promptId) => {
      if (promptId == null) return null;
      const question = questions.find((q) => Number(q?.id) === Number(questionId));
      const prompts = question?.meta?.questionPrompts || [];
      return prompts.find((p) => Number(p.id) === Number(promptId)) || null;
    };

    const countStemsFor = (question) =>
      Object.values(allStems || {}).filter((stem) => {
        const stemQuestionId = stem.questionId || stem.question_id;
        return (
          stemQuestionId != null && Number(stemQuestionId) === Number(question.id)
        );
      }).length;

    // Regra: fluxo interativo = 1 unidade (tudo-ou-nada).
    // Híbrida (interativa + stems): 1 + stems. Case: stems (ou 1 se sem stems).
    const totalUnits = questions.reduce((sum, question) => {
      if (!question) return sum;
      if (Number(question.version) === 1) return sum + 1;

      const stemsCount = countStemsFor(question);

      if (question.format === "interactive") {
        return sum + 1 + stemsCount;
      }

      return sum + (stemsCount > 0 ? stemsCount : 1);
    }, 0);

    if (totalUnits <= 0) return false;

    const answeredUnitKeys = new Set();

    mockAnswers.forEach((answer) => {
      if (!answer?.questionId) return;

      if (answer?.questionPromptId != null) {
        // Só contamos o fluxo como "respondido" quando o aluno chega num prompt is_final.
        const prompt = findPromptById(answer.questionId, answer.questionPromptId);
        if (prompt?.isFinal === true) {
          answeredUnitKeys.add(`flow-${answer.questionId}`);
        }
        return;
      }

      if (answer?.questionStemId != null) {
        answeredUnitKeys.add(`stem-${answer.questionId}-${answer.questionStemId}`);
        return;
      }

      answeredUnitKeys.add(`question-${answer.questionId}`);
    });

    return answeredUnitKeys.size >= totalUnits;
  }
);

export const makeIsFirstFinishedMock = createSelector(
  getAllMocks,
  (mocks) => mocks.filter((mock) => mock.finishedAt).length === 1
);

export const getAllMockTemplates = createSelector(
  getMockTemplates,
  (mockTemplates) => Object.values(mockTemplates)
);

export const getMockTemplateById = createSelector(
  getAllMockTemplates,
  getProps,
  (mockTemplates, id) =>
    mockTemplates.find((mockTemplate) => mockTemplate.id == id)
);

export const levelsWithResultsFromMockTemplate = createSelector(
  getCurrentMock,
  getMockTemplateById,
  (mock, mockTemplate) =>
    mockTemplate?.levels
      ?.map((levelMockTemplate) => {
        const levelWithResult = mock?.results?.find(
          (level) => levelMockTemplate.levelId == level.id
        );

        return {
          ...levelMockTemplate,
          name: levelMockTemplate.name.split(" ")[0],
          countTrue: levelWithResult?.countTrue,
          countFalse: levelWithResult?.countFalse,
        };
      })
      .sort(orderByName)
);

export const getResumeMock = createSelector(getAllMocks, (mocks) =>
  mocks.find(({ finishedAt }) => !finishedAt)
);
